import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Bookmark,
  Factory,
  LayoutDashboard,
  List,
  Plug,
  Route as RouteIcon,
  Search,
  Settings,
} from "lucide-react";
import { useSavedViews } from "@/hooks/useSavedViews";
import { writeFilters } from "@/hooks/useUrlFilters";
import { t } from "@/lib/i18n";

type Entry = {
  id: string;
  label: string;
  hint?: string;
  Icon: typeof Search;
  onSelect: () => void;
};

const NAV_ENTRIES: Omit<Entry, "onSelect">[] = [
  { id: "nav-overview", label: t("Übersicht"), hint: t("Dashboard"), Icon: LayoutDashboard },
  { id: "nav-logs", label: t("Logs"), hint: t("Live-Stream"), Icon: List },
  { id: "nav-traces", label: t("Traces"), hint: t("Trace-Browser"), Icon: RouteIcon },
  { id: "nav-anlagen", label: t("Anlagen"), hint: t("Live-Status"), Icon: Factory },
  { id: "nav-quellen", label: t("Quellen"), hint: t("TCP / Webhook / Loki"), Icon: Plug },
  { id: "nav-alarmierung", label: t("Alarmierung"), hint: t("Teams-Channels"), Icon: Bell },
  { id: "nav-einstellungen", label: t("Einstellungen"), hint: t("Benutzer / Lizenz / Updates"), Icon: Settings },
];

const NAV_PATHS: Record<string, string> = {
  "nav-overview": "/uebersicht",
  "nav-logs": "/logs",
  "nav-traces": "/traces",
  "nav-anlagen": "/anlagen",
  "nav-quellen": "/quellen",
  "nav-alarmierung": "/alarmierung",
  "nav-einstellungen": "/einstellungen",
};

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { views } = useSavedViews();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const entries: Entry[] = useMemo(() => {
    const navs: Entry[] = NAV_ENTRIES.map((e) => ({
      ...e,
      onSelect: () => navigate(NAV_PATHS[e.id]),
    }));
    const saved: Entry[] = views.map((v) => ({
      id: `view-${v.id}`,
      label: v.name,
      hint: t("Saved View"),
      Icon: Bookmark,
      onSelect: () => navigate({ pathname: "/logs", search: writeFilters(v.filters).toString() }),
    }));
    const trimmed = query.trim();
    const looksLikeTrace = trimmed.length >= 8 && /^[a-z0-9\-_]+$/i.test(trimmed);
    const traceJump: Entry[] = looksLikeTrace
      ? [
          {
            id: "trace-jump",
            label: t("Trace öffnen: {id}", { id: trimmed }),
            hint: t("Direkt zur Trace-Timeline"),
            Icon: RouteIcon,
            onSelect: () => navigate(`/traces/${encodeURIComponent(trimmed)}`),
          },
        ]
      : [];
    return [...traceJump, ...navs, ...saved];
  }, [navigate, views, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.id.startsWith("trace-jump") ||
        e.label.toLowerCase().includes(q) ||
        (e.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [entries, query]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = filtered[active];
        if (sel) {
          sel.onSelect();
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, filtered, active]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/30 p-3 pt-[max(1rem,env(safe-area-inset-top,0px))] backdrop-blur-sm md:pt-[12vh]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[min(560px,92vw)] overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Springen zu Bereich, Saved View oder Trace-ID…")}
            className="h-11 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground md:inline">
            esc
          </kbd>
        </div>

        <ul className="max-h-[60vh] overflow-y-auto p-1.5">
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t("Keine Treffer.")}
            </li>
          ) : (
            filtered.map((e, i) => (
              <li key={e.id}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => {
                    e.onSelect();
                    onClose();
                  }}
                  className={
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors " +
                    (i === active
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-muted/40")
                  }
                >
                  <e.Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{e.label}</span>
                  {e.hint ? (
                    <span className="shrink-0 text-[11px] text-muted-foreground">{e.hint}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

